import EventEmitter from 'eventemitter3'
import type { IToolbarConfig } from './IToolbarConfig'
import { DEFAULT_TOOLBAR_CONFIG } from './IToolbarConfig'
import '../../styles/toolbar.scss'
import { ToolbarButtonElement } from './ToolbarButtonElement'
import { ToolbarDropdownElement } from './ToolbarDropdownElement'
import { ToolbarColorElement } from './ToolbarColorElement'
import { ToolbarBackgroundElement } from './ToolbarBackgroundElement'
import { ToolbarListElement } from './ToolbarListElement'
import type { ToolbarElement } from './ToolbarElement'
import { ToolbarBooleanButtonElement } from './ToolbarBooleanButtonElement'
import { ToolbarFontSizeElement } from './ToolbarFontSizeElement'
import { ToolbarTableElement } from './ToolbarTableElement'
import { ToolbarSearchElement } from './ToolbarSearchElement'
import { ToolbarLinkElement } from './ToolbarLinkElement'
import { ToolbarImageElement } from './ToolbarImageElement'
import type { IRange, Document, SearchService, SelectionService, ISearchResult, ContentService } from '@fast-editor/core'
import { SelectionServiceEvent, SearchServiceEvent, DocumentEvent } from '@fast-editor/core'
import type { Editor } from 'src/Editor'
import Controller from '../Controller'

const toolbarTemplate = `
  <div class="toolbar-buttons">
    <div class="toolbar-button-expand toolbar-button"></div>
  </div>
  <div class="toolbar-expand-buttons">
  </div>
`

export class ToolbarController extends Controller {
  public readonly config: IToolbarConfig
  public toolbarFormatElements: Map<string, ToolbarElement> = new Map()
  public toolbarSearchElements: Map<string, ToolbarElement> = new Map()
  public toolbarHistoryElements: Map<string, ToolbarElement> = new Map()
  public toolbarSelectionElements: Map<string, ToolbarElement> = new Map()
  public currentFormat: { [key: string]: Set<any> } = {}
  public hasChange = false

  private contentService: ContentService
  private searchService: SearchService
  private selectionService: SelectionService

  constructor(
    editor: Editor,
    doc: Document,
    contentService: ContentService,
    searchService: SearchService,
    selectionService: SelectionService,
    protected readonly container: HTMLDivElement,
    protected readonly overlayContainer: HTMLDivElement,
    config: Partial<IToolbarConfig>,
  ) {
    super(editor, doc)
    this.contentService = contentService
    this.searchService = searchService
    this.selectionService = selectionService
    this.config = { ...config, ...DEFAULT_TOOLBAR_CONFIG }
    this.initDom()
    this.initEvent()
  }

  public flattenFormat(format: { [key: string]: Set<any> }) {
    const result: { [key: string]: any } = {}
    Object.keys(format).forEach((key) => {
      const valueSet = format[key]
      if (valueSet.size === 1) {
        result[key] = valueSet.values().next().value
      }
    })
    return result
  }

  public setHistoryStatus(status: { canUndo: boolean; canRedo: boolean; stackLength: number; current: number }) {
    this.toolbarHistoryElements.forEach((element) => {
      element.setHistoryStatus(status)
    })
  }

  protected initEvent() {
    this.searchService.addListener(SearchServiceEvent.SEARCH_RESULT_CHANGE, (result: ISearchResult[], index) => {
      this.toolbarSearchElements.forEach((element) => {
        element.setSearchResult(index, result.length)
      })
    })
    this.selectionService.addListener(SelectionServiceEvent.CHANGE_SELECTION, (selection: IRange[]) => {
      this.getCurrentFormat()
      this.toolbarSelectionElements.forEach((element) => {
        element.setSelection(selection)
      })
    })
    this.doc.em.addListener(DocumentEvent.DOCUMENT_CHANGE_CONTENT, this.getCurrentFormat)
  }

  protected initDom() {
    this.container.classList.add('toolbar-container')
    this.container.innerHTML = toolbarTemplate
    this.container.style.height = `${this.config.height}px`
    this.createFirstLine()
    this.createExpandButtons()
  }

  protected createFirstLine() {
    const firstLine = this.container.querySelector('div.toolbar-buttons') as HTMLDivElement
    this.config.buttons[0]?.forEach((buttonName: string) => {
      const button = this.createToolbarElement(buttonName)
      if (button) {
        firstLine.append(button)
      }
    })
  }

  protected createExpandButtons() {
    const expandButtonContainer = this.container.querySelector('div.toolbar-expand-buttons') as HTMLDivElement
    if (this.config.buttons.length > 1) {
      const expandButton = this.container.querySelector('div.toolbar-button-expand') as HTMLDivElement
      expandButton.style.display = 'block'
      expandButton.addEventListener('click', () => {
        expandButtonContainer.style.display = expandButtonContainer.style.display === 'block' ? 'none' : 'block'
      })
      expandButton.addEventListener('mousedown', (event) => {
        event.preventDefault()
      })
    }
    for (let index = 1; index < this.config.buttons.length; index++) {
      const line = document.createElement('div')
      line.classList.add('toolbar-expand-buttons-line')
      const lineButtonName = this.config.buttons[index]
      lineButtonName?.forEach((buttonName: string) => {
        const button = this.createToolbarElement(buttonName)
        if (button) {
          line.append(button)
        }
      })
      expandButtonContainer.append(line)
    }
  }

  protected createToolbarElement(name: string): HTMLElement | null {
    switch (name) {
      case 'splitter': {
        const button = new ToolbarButtonElement(this.overlayContainer, name)
        this.toolbarFormatElements.set(name, button)
        return button.getEntryElement()
      }
      case 'undo':
      case 'redo': {
        const button = new ToolbarButtonElement(this.overlayContainer, name)
        button.on('click', () => {
          if (name === 'undo') {
            this.editor.undo()
          } else {
            this.editor.redo()
          }
        })
        this.toolbarHistoryElements.set(name, button)
        return button.getEntryElement()
      }
      case 'clearFormat': {
        const button = new ToolbarButtonElement(this.overlayContainer, name)
        button.on('click', () => {
          this.editor.clearFormat()
        })
        this.toolbarFormatElements.set(name, button)
        return button.getEntryElement()
      }
      case 'bold':
      case 'italic':
      case 'underline':
      case 'strike': {
        const button = new ToolbarBooleanButtonElement(this.overlayContainer, name)
        button.on('click', (status) => {
          this.emitFormat({ [name]: status })
        })
        this.toolbarFormatElements.set(name, button)
        return button.getEntryElement()
      }
      case 'indentRight':
      case 'indentLeft': {
        const button = new ToolbarButtonElement(this.overlayContainer, name)
        button.on('click', () => {
          this.editor.setIndent(name === 'indentRight')
        })
        this.toolbarFormatElements.set(name, button)
        return button.getEntryElement()
      }
      case 'code': {
        const button = new ToolbarBooleanButtonElement(this.overlayContainer, name)
        button.on('click', (status) => {
          this.editor.setCodeBlock()
        })
        this.toolbarFormatElements.set(name, button)
        return button.getEntryElement()
      }
      case 'quote': {
        const button = new ToolbarBooleanButtonElement(this.overlayContainer, name)
        button.on('click', () => {
          this.editor.setQuoteBlock()
        })
        this.toolbarFormatElements.set(name, button)
        return button.getEntryElement()
      }
      case 'table': {
        const tableDropdown = new ToolbarTableElement(this.overlayContainer, name, {
          rowCount: 8,
          colCount: 8,
          overlayElementWidth: 180,
          entryElementHeight: 32,
          topOffset: 9,
        })
        tableDropdown.on('choose', (rowIndex, colIndex) => {
          this.editor.insertTable(rowIndex, colIndex)
        })
        this.toolbarFormatElements.set(name, tableDropdown)
        return tableDropdown.getEntryElement()
      }
      case 'title': {
        const dropdown = new ToolbarDropdownElement(this.overlayContainer, name, {
          items: [
            { key: 'text', value: -1, text: 'Text', default: true },
            { key: 'title', value: 0, text: 'Title' },
            { key: 'subtitle', value: 1, text: 'SubTitle' },
            { key: 'h1', value: 2, text: 'Heading 1' },
            { key: 'h2', value: 3, text: 'Heading 2' },
            { key: 'h3', value: 4, text: 'Heading 3' },
            { key: 'h4', value: 5, text: 'Heading 4' },
          ],
          overlayElementWidth: 130,
          entryElementHeight: 32,
          topOffset: 9,
        })
        dropdown.on('choose', (value) => {
          this.emitFormat({ title: parseInt(value, 10) })
        })
        this.toolbarFormatElements.set(name, dropdown)
        return dropdown.getEntryElement()
      }
      case 'font': {
        const dropdown = new ToolbarDropdownElement(this.overlayContainer, name, {
          items: [
            { key: 'default', value: 'Default', text: ' 默认字体', default: true },
            { key: 'simsun', value: 'simsun', text: '宋体' },
            { key: 'simhei', value: 'simhei', text: '黑体' },
            { key: 'fangsong', value: 'fangsong', text: '仿宋' },
            { key: 'kaiti', value: 'kaiti', text: '楷体' },
            { key: 'arial', value: 'arial', text: 'Arial' },
            { key: 'droid', value: 'droid', text: 'Droid' },
            { key: 'source', value: 'source', text: 'Source Code Pro' },
          ],
          overlayElementWidth: 155,
          entryElementHeight: 32,
          topOffset: 9,
        })
        dropdown.on('choose', (value) => {
          this.emitFormat({ font: value })
        })
        this.toolbarFormatElements.set(name, dropdown)
        return dropdown.getEntryElement()
      }
      case 'size': {
        const dropdown = new ToolbarFontSizeElement(this.overlayContainer, name, {
          items: this.config.fontSize.map((size: number) => ({
            key: size.toString(),
            value: size,
            text: size.toString(),
            default: size === 11,
          })),
          overlayElementWidth: 55,
          entryElementHeight: 32,
          topOffset: 9,
        })
        dropdown.on('choose', (value) => {
          this.emitFormat({ size: parseInt(value, 10) })
        })
        this.toolbarFormatElements.set(name, dropdown)
        return dropdown.getEntryElement()
      }
      case 'align': {
        const dropdown = new ToolbarDropdownElement(this.overlayContainer, name, {
          items: [
            { key: 'left', value: 'left', text: '', default: true },
            { key: 'center', value: 'center', text: '' },
            { key: 'right', value: 'right', text: '' },
            { key: 'justify', value: 'justify', text: '' },
          ],
          overlayElementWidth: 60,
          entryElementHeight: 32,
          topOffset: 9,
        })
        dropdown.on('choose', (value) => {
          this.emitFormat({ align: value })
        })
        this.toolbarFormatElements.set(name, dropdown)
        return dropdown.getEntryElement()
      }
      case 'linespacing': {
        const dropdown = new ToolbarDropdownElement(this.overlayContainer, name, {
          items: [
            { key: '1', value: '1', text: '1', default: true },
            { key: '1.15', value: '1.15', text: '1.15' },
            { key: '1.3', value: '1.3', text: '1.3' },
            { key: '1.5', value: '1.5', text: '1.5' },
            { key: '1.7', value: '1.7', text: '1.7' },
            { key: '2', value: '2', text: '2' },
            { key: '3', value: '3', text: '3' },
          ],
          overlayElementWidth: 60,
          entryElementHeight: 32,
          topOffset: 9,
        })
        dropdown.on('choose', (linespacing) => {
          this.emitFormat({ linespacing })
        })
        this.toolbarFormatElements.set(name, dropdown)
        return dropdown.getEntryElement()
      }
      case 'color': {
        const splitDropdown = new ToolbarColorElement(this.overlayContainer, 'color', {
          overlayElementWidth: 280,
          entryElementHeight: 32,
          topOffset: 9,
        })
        splitDropdown.on('choose', (color) => {
          this.emitFormat({ color })
        })
        this.toolbarFormatElements.set(name, splitDropdown)
        return splitDropdown.getEntryElement()
      }
      case 'background': {
        const splitDropdown = new ToolbarBackgroundElement(this.overlayContainer, 'background', {
          overlayElementWidth: 280,
          entryElementHeight: 32,
          topOffset: 9,
        })
        splitDropdown.on('choose', (background) => {
          this.emitFormat({ background })
        })
        this.toolbarFormatElements.set(name, splitDropdown)
        return splitDropdown.getEntryElement()
      }
      case 'list': {
        const splitDropdown = new ToolbarListElement(this.overlayContainer, 'list', {
          overlayElementWidth: 250,
          entryElementHeight: 32,
          topOffset: 9,
        })
        this.toolbarFormatElements.set(name, splitDropdown)
        return splitDropdown.getEntryElement()
      }
      case 'search': {
        const search = new ToolbarSearchElement(this.overlayContainer, name)
        search.on('search', (keywords: string, caseSensitive: boolean) => {
          this.editor.search(keywords, caseSensitive)
        })
        search.on('search-prev', () => {
          this.editor.prevSearchResult()
        })
        search.on('search-next', () => {
          this.editor.nextSearchResult()
        })
        search.on('search-clear', () => {
          this.editor.clearSearch()
        })
        search.on('search-replace', (replaceWords, all) => {
          this.editor.replace(replaceWords, all)
        })
        this.toolbarSearchElements.set(name, search)
        return search.getEntryElement()
      }
      case 'link': {
        const link = new ToolbarLinkElement(this.overlayContainer, name)
        link.on('set-link', (url, text) => {
          this.editor.setLink(url, text)
        })
        this.toolbarSelectionElements.set(name, link)
        return link.getEntryElement()
      }
      case 'image': {
        const image = new ToolbarImageElement(this.overlayContainer, name)
        image.on('insert-image', (url, width: number, height: number) => {
          this.editor.insertImage(url, width, height)
        })
        return image.getEntryElement()
      }
      default: {
        if (typeof this.config.createToolbarElement === 'function') {
          const element = this.config.createToolbarElement(name, this)
          if (element) {
            this.toolbarFormatElements.set(name, element)
            return element.getEntryElement()
          }
        }
        return null
      }
    }
  }

  protected emitFormat(format: { [key: string]: any }) {
    this.editor.setFormat(format)
    let hasChange = false
    Object.keys(format).forEach((key) => {
      const valueSet = this.currentFormat[key]
      if (valueSet.size !== 1 || valueSet.values().next().value !== format[key]) {
        this.currentFormat[key] = new Set([format[key]])
        hasChange = true
      }
    })
    this.hasChange = hasChange
    if (hasChange) {
      this.toolbarFormatElements.forEach((element) => {
        element.setFormat(this.currentFormat)
      })
    }
  }

  private getCurrentFormat = () => {
    let format = {}
    const selection = this.selectionService.getSelection()
    if (selection && selection.length > 0) {
      format = this.contentService.getFormat(selection)
    }
    this.setFormat(format)
  }

  private setFormat(format: { [key: string]: Set<any> }) {
    this.hasChange = false
    this.currentFormat = format
    this.toolbarFormatElements.forEach((element) => {
      element.setFormat(format)
    })
  }
}
